import type { ReactNode } from "react";

import type {
  IframeMessage,
  IframeMessageOptions,
  IframeReadyMessage,
  UrlChangeMessage,
} from "./iframe-message-types";
import { useIframeUrlTracking } from "./use-iframe-url-tracking";

export type { IframeMessage, IframeReadyMessage, UrlChangeMessage };

export type IframeUrlTrackerOptions = IframeMessageOptions;

export class IframeUrlTracker {
  private currentPath: string;
  private parentOrigin: string;
  private observer: MutationObserver | null = null;
  private enabled: boolean;
  private popstateListener: (() => void) | null = null;

  constructor(options: IframeUrlTrackerOptions = {}) {
    this.currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    this.parentOrigin = options.parentOrigin || "*";
    this.enabled = options.enabled ?? true;

    if (this.enabled && this.isInIframe()) {
      this.initialize();
    }
  }

  private isInIframe(): boolean {
    return typeof window !== "undefined" && window.parent !== window;
  }

  private initialize(): void {
    if (typeof window === "undefined") return;

    // Send ready message first
    this.sendReady();

    // Send initial path
    this.sendUrlChange(this.currentPath);

    // Set up popstate listener for back/forward navigation
    this.popstateListener = () => {
      this.currentPath = window.location.pathname;
      this.sendUrlChange(this.currentPath);
    };
    window.addEventListener("popstate", this.popstateListener);

    // Set up MutationObserver for SPA navigation detection
    this.observer = new MutationObserver(() => {
      if (window.location.pathname !== this.currentPath) {
        this.currentPath = window.location.pathname;
        this.sendUrlChange(this.currentPath);
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private sendReady(): void {
    if (!this.isInIframe()) return;

    const message: IframeReadyMessage = {
      type: "iframe-ready",
    };

    window.parent.postMessage(message, this.parentOrigin);
  }

  private sendUrlChange(path: string): void {
    if (!this.isInIframe()) return;

    const message: UrlChangeMessage = {
      type: "urlChange",
      path,
    };

    window.parent.postMessage(message, this.parentOrigin);
  }

  updatePath(path: string): void {
    if (path !== this.currentPath) {
      this.currentPath = path;
      if (this.enabled && this.isInIframe()) {
        this.sendUrlChange(path);
      }
    }
  }

  sendReadyMessage(): void {
    if (this.enabled && this.isInIframe()) {
      this.sendReady();
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    if (enabled && this.isInIframe() && !this.observer) {
      this.initialize();
    } else if (!enabled) {
      this.destroy();
    }
  }

  destroy(): void {
    this.enabled = false;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.popstateListener) {
      window.removeEventListener("popstate", this.popstateListener);
      this.popstateListener = null;
    }
  }
}

interface IframeWrapperProps {
  children: ReactNode;
}

export function IframeWrapper({ children }: IframeWrapperProps) {
  useIframeUrlTracking({
    parentOrigin: "*", // Configure as needed for security
    enabled: true,
  });

  return <>{children}</>;
}
